import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { SectionPaper, SectionTitle, LineItemRow, NumberTextField } from './styledComponents';
import { formatCurrency } from './utils';
import ProductSelector from './ProductSelector';

const PILineItemsSection = ({
  lineItems,
  products,
  productVariants,
  purchaseOrder,
  priceLists,
  currentCompany,
  onLineItemsChange,
  onLineItemChange,
  onAddLineItem,
  onRemoveLineItem,
  onFetchProductVariants,
  onFetchInventoryItems,
  onSetSelectedLocation,
  onUpdate,
  companyId,
  canEdit = true,
  canDelete = true
}) => {
  const theme = useTheme();
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [targetLineItemIndex, setTargetLineItemIndex] = useState(null);
  const [priceListItems, setPriceListItems] = useState([]);
  const [originalLineItems, setOriginalLineItems] = useState([]);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null, item: null });
  const [priceListDialogOpen, setPriceListDialogOpen] = useState(false);
  const [priceListSearchTerm, setPriceListSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [massDeleteConfirmOpen, setMassDeleteConfirmOpen] = useState(false);

  
  // Track manually edited descriptions to prevent auto-rebuild from overwriting them
  const [manuallyEditedDescriptions, setManuallyEditedDescriptions] = useState(new Set());

  // Rebuild descriptions ONLY when loading from database/product selections (not when user manually edits)
  // Skip rebuilding if description was manually edited
  useEffect(() => {
    if (lineItems.length > 0) {
      const rebuiltItems = lineItems.map((item, index) => {
        // If no product_name, don't change description (might be empty/new item)
        if (!item.product_name) return item;
        
        // Skip if this description was manually edited
        const itemKey = `${item.product_id}-${item.variant_id}-${index}`;
        if (manuallyEditedDescriptions.has(itemKey)) {
          return item; // Preserve manually edited description
        }
        
        // Calculate what the auto-generated description should be
        let expectedDescription = '';
        if (item.lot_number) {
          if (item.variant_name) {
            expectedDescription = `${item.product_name} - ${item.variant_name} (Lot: ${item.lot_number || 'N/A'})`;
          } else {
            expectedDescription = `${item.product_name} (Lot: ${item.lot_number || 'N/A'})`;
          }
        } else if (item.variant_name) {
          expectedDescription = `${item.product_name} - ${item.variant_name}`;
        } else {
          expectedDescription = item.product_name;
        }
        
        // Only rebuild if description is empty/undefined or matches expected pattern
        const isEmpty = !item.description || item.description.trim() === '';
        const matchesExpected = item.description === expectedDescription;
        
        if (isEmpty || matchesExpected) {
          if (item.description !== expectedDescription) {
            return {
              ...item,
              description: expectedDescription
            };
          }
        }
        
        return item;
      });
      
      // Check if any descriptions changed
      const descriptionsChanged = rebuiltItems.some((item, idx) => 
        item.description !== lineItems[idx]?.description
      );
      
      if (descriptionsChanged) {
        onLineItemsChange(rebuiltItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems.map(item => `${item.product_id}-${item.variant_id}-${item.product_name}-${item.variant_name}-${item.lot_number}`).join('|')]);

  // Initialize original line items when they first load
  useEffect(() => {
    if (lineItems.length > 0 && originalLineItems.length === 0) {
      const initOriginals = lineItems.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        unit_type: item.unit_type,
        unit_price: item.unit_price
      }));
      setOriginalLineItems(initOriginals);
    }
  }, [lineItems, originalLineItems.length]);

  // Fetch price list items when price list changes
  useEffect(() => {
    if (purchaseOrder?.price_list_id && companyId) {
      fetchPriceListItems(purchaseOrder.price_list_id);
    } else {
      setPriceListItems([]);
    }
  }, [purchaseOrder?.price_list_id, companyId]);



  const fetchPriceListItems = async (priceListId) => {
    try {
      const { data, error } = await supabase
        .from('price_list_items')
        .select('*')
        .eq('price_list_id', priceListId);

      if (error) throw error;
      setPriceListItems(data || []);
    } catch (err) {
      console.error('Error fetching price list items:', err);
      setPriceListItems([]);
    }
  };

  // Find the price list item for a given line item
  const getPriceListItem = (item) => {
    if (!item.product_id || priceListItems.length === 0) return null;
    return priceListItems.find(p =>
      p.product_id === item.product_id &&
      (item.variant_id ? p.variant_id === item.variant_id : !p.variant_id)
    ) || null;
  };

  // Apply a price tier (case/pallet) from the price list to a line item
  const handlePriceTierChange = (index, tier) => {
    const item = lineItems[index];
    const plItem = getPriceListItem(item);
    if (!plItem) return;
    const newPrice = tier === 'pallet'
      ? parseFloat(plItem.pallet_price || plItem.case_price || 0)
      : parseFloat(plItem.case_price || plItem.price || 0);
    const qty = parseFloat(item.quantity) || 0;
    const updated = lineItems.map((li, i) => i === index
      ? { ...li, price_tier: tier, unit_price: newPrice, line_total: qty * newPrice }
      : li
    );
    onLineItemsChange(updated);
  };

  const handleOpenProductDialog = (index = null) => {
    setTargetLineItemIndex(index);
    setShowProductDialog(true);
  };

  const handleCloseProductDialog = () => {
    setShowProductDialog(false);
    setTargetLineItemIndex(null);
  };

  const handleItemsAdded = (newItems) => {
    const updatedOriginals = [...originalLineItems];
    let updatedItems;

    if (targetLineItemIndex !== null && newItems.length > 0) {
      // Replace the targeted row; if multiple lots selected, insert the rest after it
      updatedItems = [...lineItems];
      updatedItems.splice(targetLineItemIndex, 1, ...newItems);
      updatedOriginals.splice(targetLineItemIndex, 1, ...newItems.map(item => ({
        product_id: item.product_id,
        variant_id: item.variant_id,
        quantity: item.quantity,
        unit_price: item.unit_price
      })));
    } else {
      updatedItems = [...lineItems, ...newItems];
      newItems.forEach((item, idx) => {
        updatedOriginals[lineItems.length + idx] = {
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          unit_price: item.unit_price
        };
      });
    }

    setOriginalLineItems(updatedOriginals);
    onLineItemsChange(updatedItems);
  };


  const handleRemoveLineItem = (index) => {
    const item = lineItems[index];
    // Show confirmation dialog
    setDeleteConfirmDialog({ open: true, index, item });
  };

  const handleConfirmDelete = () => {
    const { index } = deleteConfirmDialog;
    // Remove from original items tracking as well
    const updatedOriginals = [...originalLineItems];
    updatedOriginals.splice(index, 1);
    setOriginalLineItems(updatedOriginals);
    
    // Call parent's remove function
    onRemoveLineItem(index);
    
    // Remove from selection and re-index remaining selections
    setSelectedRows(prev => {
      const next = new Set();
      prev.forEach(i => { if (i < index) next.add(i); else if (i > index) next.add(i - 1); });
      return next;
    });

    // Close dialog
    setDeleteConfirmDialog({ open: false, index: null, item: null });
  };






  const handleDeleteSelected = () => {
    const updatedItems = lineItems.filter((_, i) => !selectedRows.has(i));
    const updatedOriginals = originalLineItems.filter((_, i) => !selectedRows.has(i));
    setOriginalLineItems(updatedOriginals);
    onLineItemsChange(updatedItems);
    setSelectedRows(new Set());
  };

  const getFilteredPriceLists = () => {
    if (!priceLists) return [];
    if (!priceListSearchTerm.trim()) return priceLists;
    const term = priceListSearchTerm.toLowerCase();
    return priceLists.filter(list =>
      list.name.toLowerCase().includes(term) ||
      list.type.toLowerCase().includes(term)
    );
  };

  const handlePriceListSelect = (priceListId) => {
    const selectedList = priceLists?.find(pl => pl.id === priceListId);
    const priceListName = selectedList ? `${selectedList.name} (${selectedList.type})` : '';
    onUpdate({
      price_list_id: priceListId,
      price_list_name: priceListName
    });
    setPriceListDialogOpen(false);
    setPriceListSearchTerm('');
  };

  const renderProductCell = (item) => {
    return (
      <TableCell>
        {item.product_id ? (
          <Typography variant="body2" fontWeight={500} sx={{ px: 1 }}>
            {item.product_name || 'Unknown Product'}
          </Typography>
        ) : null}
      </TableCell>
    );
  };

  return (
    <>
      <SectionPaper sx={{ width: '100%', mt: 3 }}>
        <SectionTitle variant="h6">
          <ReceiptIcon />
          Line Items
        </SectionTitle>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
            Price List
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => canEdit && setPriceListDialogOpen(true)}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: 'white',
              minHeight: 56,
              textAlign: 'left',
              justifyContent: 'flex-start',
              px: 3,
              py: 2.5,
              opacity: canEdit ? 1 : 0.3,
              pointerEvents: canEdit ? 'auto' : 'none',
              cursor: canEdit ? 'pointer' : 'not-allowed',
              '&:hover': {
                border: `1px solid ${theme.palette.primary.main}`,
                boxShadow: canEdit ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}` : 'none'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {purchaseOrder.price_list_id ? (
                <>
                  <Typography variant="body1" fontWeight={500}>
                    {purchaseOrder.price_list_name || (() => {
                      const list = priceLists?.find(pl => pl.id === purchaseOrder.price_list_id);
                      return list ? `${list.name} (${list.type})` : 'Price List Selected';
                    })()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentCompany?.name || 'Your Company'}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" fontWeight={500}>
                    Standard Retail Prices
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentCompany?.name || 'Your Company'} - Default Product Pricing
                  </Typography>
                </>
              )}
            </Box>
          </Button>
        </Box>

        <TableContainer sx={{
          overflowX: 'auto',
          borderRadius: 3,
          backgroundColor: '#ffffff',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`,
        }}>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                '& .MuiTableCell-root': {
                  borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  color: 'primary.main'
                }
              }}>
                <TableCell sx={{ width: 140 }}>Product</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Qty</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>Size</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>Lot Number</TableCell>
                <TableCell align="center" sx={{ width: 110 }}>Mfg Date</TableCell>
                <TableCell align="center" sx={{ width: 110 }}>Exp Date</TableCell>
                <TableCell align="center" sx={{ width: 120 }}>Price</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>Amount</TableCell>
                <TableCell width={50} align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map((item, index) => (
                <LineItemRow key={item.id || index}>
                  {renderProductCell(item)}

                  <TableCell align="center">
                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.product_id ? (item.quantity ?? '-') : '-'}
                      </Typography>
                      {item.unit_type && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 500,
                            color: item.unit_type === 'Unit' ? 'info.main' : 'success.main'
                          }}
                        >
                          {item.unit_type === 'Unit' ? 'Units' : 'Cases'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: item.variant_name ? 'text.primary' : 'text.secondary' }}>
                      {item.variant_name || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: item.lot_number ? 'text.primary' : 'text.secondary' }}>
                      {item.lot_number || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: item.manufactured_date ? 'text.primary' : 'text.secondary' }}>
                      {item.manufactured_date ? new Date(item.manufactured_date).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: item.expiry_date ? 'text.primary' : 'text.secondary' }}>
                      {item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    {(() => {
                      const plItem = getPriceListItem(item);
                      const hasPallet = plItem && parseFloat(plItem.pallet_price) > 0;
                      const tier = item.price_tier || 'case';
                      return (
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                          <NumberTextField
                            type="number"
                            value={item.unit_price === 0 || item.unit_price === '' || item.unit_price === null || item.unit_price === undefined ? '' : item.unit_price}
                            onChange={(e) => {
                              if (canEdit) {
                                const val = e.target.value;
                                if (val === '' || val === null || val === undefined) {
                                  onLineItemChange(index, 'unit_price', '');
                                } else {
                                  onLineItemChange(index, 'unit_price', val);
                                }
                              }
                            }}
                            onBlur={(e) => {
                              if (canEdit) {
                                const val = parseFloat(e.target.value);
                                if (isNaN(val) || val < 0) {
                                  onLineItemChange(index, 'unit_price', '0');
                                }
                              }
                            }}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            size="small"
                            disabled={!canEdit}
                            sx={{ width: '110px', opacity: canEdit ? 1 : 0.6 }}
                          />
                          {hasPallet && canEdit && (
                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                              <Typography
                                variant="caption"
                                onClick={() => handlePriceTierChange(index, 'case')}
                                sx={{ cursor: 'pointer', color: tier === 'case' ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}
                              >
                                Case
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.disabled' }}>·</Typography>
                              <Typography
                                variant="caption"
                                onClick={() => handlePriceTierChange(index, 'pallet')}
                                sx={{ cursor: 'pointer', color: tier === 'pallet' ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}
                              >
                                Pallet
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title={formatCurrency(item.line_total || item.total_price)} arrow placement="left">
                      <Typography sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '120px',
                        fontWeight: 500,
                        color: 'success.main',
                        fontSize: '0.9rem'
                      }}>
                        {formatCurrency(item.line_total || item.total_price)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => canDelete && handleRemoveLineItem(index)}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: 'white',
                        opacity: canDelete ? 1 : 0.3,
                        pointerEvents: canDelete ? 'auto' : 'none',
                        cursor: canDelete ? 'pointer' : 'not-allowed',
                        '&:hover': {
                          transform: canDelete ? 'scale(1.1)' : 'none',
                          border: `1px solid ${theme.palette.error.main}`,
                          boxShadow: canDelete ? `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}` : 'none'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </LineItemRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            startIcon={<AddIcon />} 
            onClick={canEdit ? () => handleOpenProductDialog() : undefined}
            variant="outlined"
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              px: 3,
              py: 1.5,
              background: 'white',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              opacity: canEdit ? 1 : 0.3,
              pointerEvents: canEdit ? 'auto' : 'none',
              cursor: canEdit ? 'pointer' : 'not-allowed',
              '&:hover': {
                transform: canEdit ? 'translateY(-1px)' : 'none',
                boxShadow: canEdit ? `0 4px 12px ${alpha(theme.palette.grey[400], 0.25)}` : 'none'
              }
            }}
          >
            Add Line Item
          </Button>

          {selectedRows.size > 0 && canDelete && (
            <Button
              startIcon={<DeleteIcon />}
              onClick={() => setMassDeleteConfirmOpen(true)}
              variant="outlined"
              color="error"
              sx={{
                borderRadius: 2,
                fontWeight: 500,
                px: 3,
                py: 1.5,
                background: 'white',
              }}
            >
              Delete Selected ({selectedRows.size})
            </Button>
          )}
        </Box>
      </SectionPaper>

      {/* Product Summary */}
      {(() => {
        // Aggregate line items by product, variant, and unit type
        const summaryMap = new Map();
        
        lineItems.forEach(item => {
          if (!item.product_id) return;
          
          const key = `${item.product_id || 'no-product'}_${item.variant_id || 'no-variant'}_${item.unit_type || 'Case'}`;
          
          if (!summaryMap.has(key)) {
            summaryMap.set(key, {
              product_name: item.product_name || 'Unknown Product',
              variant_name: item.variant_name || null,
              unit_type: item.unit_type || 'Case',
              quantity: 0
            });
          }
          
          const summary = summaryMap.get(key);
          summary.quantity += parseFloat(item.quantity) || 0;
        });
        
        const summaryItems = Array.from(summaryMap.values());
        
        if (summaryItems.length === 0) return null;
        
        return (
          <SectionPaper sx={{ width: '100%', mt: 3 }}>
            <SectionTitle variant="h6">
              <ReceiptIcon />
              Product Summary
            </SectionTitle>
            <TableContainer sx={{ 
              mt: 2,
              borderRadius: 3,
              backgroundColor: '#ffffff',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`
            }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                    <TableCell sx={{ fontWeight: 500, color: 'primary.main', fontSize: '0.8rem' }}>Product</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '0.8rem' }}>Quantity</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryItems.map((summary, idx) => {
                    const unitType = summary.unit_type || 'Case';
                    const qty = summary.quantity;
                    const displayUnitType = qty > 1 
                      ? (unitType === 'Case' ? 'Cases' : unitType === 'Unit' ? 'Units' : 'Pallets')
                      : unitType;
                    
                    return (
                      <TableRow 
                        key={idx}
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.02)
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {summary.product_name}
                          </Typography>
                          {summary.variant_name && (
                            <Typography variant="caption" color="text.secondary">
                              {summary.variant_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {qty} {displayUnitType}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionPaper>
        );
      })()}

      <ProductSelector
        open={showProductDialog}
        onClose={handleCloseProductDialog}
        onItemsAdded={handleItemsAdded}
        companyId={companyId}
        purchaseOrder={purchaseOrder}
        existingLineItems={lineItems}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null, item: null })}
        maxWidth="sm"
        fullWidth
        disableScrollLock
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          fontWeight: 500,
          color: 'error.main'
        }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
            Are you sure you want to remove this line item?
          </Typography>
          {deleteConfirmDialog.item && (
            <Box sx={{ 
              p: 2, 
              bgcolor: alpha(theme.palette.grey[100], 0.5), 
              borderRadius: 2,
              mb: 2
            }}>
              <Typography variant="body2" fontWeight={500}>
                {deleteConfirmDialog.item.product_name || deleteConfirmDialog.item.description || 'Item'}
              </Typography>
              {deleteConfirmDialog.item.variant_name && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Variant: {deleteConfirmDialog.item.variant_name}
                </Typography>
              )}
              {deleteConfirmDialog.item.lot_number && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Lot: {deleteConfirmDialog.item.lot_number}
                </Typography>
              )}
              {deleteConfirmDialog.item.inventory_item_id && (
                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
                  This will unreserve the inventory lot/batch.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, index: null, item: null })}
            sx={{ borderRadius: 2, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            sx={{ 
              borderRadius: 2, 
              fontWeight: 500,
              background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mass Delete Confirmation Dialog */}
      <Dialog
        open={massDeleteConfirmOpen}
        onClose={() => setMassDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'error.main' }}>
          Delete {selectedRows.size} Line Item{selectedRows.size > 1 ? 's' : ''}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will permanently remove the selected line item{selectedRows.size > 1 ? 's' : ''} from the order. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setMassDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              handleDeleteSelected();
              setMassDeleteConfirmOpen(false);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price List Selection Dialog */}
      <Dialog
        open={priceListDialogOpen}
        onClose={() => setPriceListDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle sx={{ fontWeight: 500, color: 'primary.main' }}>
          Select Price List
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth
            placeholder="Search price lists..."
            value={priceListSearchTerm}
            onChange={(e) => setPriceListSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: !purchaseOrder.price_list_id
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                  bgcolor: !purchaseOrder.price_list_id
                    ? alpha(theme.palette.primary.main, 0.08)
                    : 'white',
                  cursor: 'pointer'
                }}
                onClick={() => handlePriceListSelect('')}
              >
                <Typography variant="body1" fontWeight={500}>
                  Standard Retail Prices
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Default Product Pricing
                </Typography>
              </Box>
            </Grid>
            {getFilteredPriceLists().map((list) => (
              <Grid item xs={12} key={list.id}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: purchaseOrder.price_list_id === list.id
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                    bgcolor: purchaseOrder.price_list_id === list.id
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'white',
                    cursor: 'pointer'
                  }}
                  onClick={() => handlePriceListSelect(list.id)}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {list.name} ({list.type})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currentCompany?.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPriceListDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => window.open(`/company/${companyId}/price-lists`, '_blank')}
          >
            Add New Price List
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PILineItemsSection;